import React, { useState, useMemo, useEffect } from 'react';
import { products, categories } from '../data/products';
import ProductCard from '../components/ProductCard';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';

const Shop = () => {
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('featured');
  const [priceMax, setPriceMax] = useState(100);
  const [availability, setAvailability] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = useMemo(() => {
    let list = products.filter(p => (category === 'All' ? true : p.category === category));
    list = list.filter(p => p.price <= priceMax);
    if (availability === 'sale') list = list.filter(p => p.compareAtPrice && p.compareAtPrice > p.price);
    switch (sort) {
      case 'price-asc': list = [...list].sort((a, b) => a.price - b.price); break;
      case 'price-desc': list = [...list].sort((a, b) => b.price - a.price); break;
      case 'az': list = [...list].sort((a, b) => a.title.localeCompare(b.title)); break;
      case 'za': list = [...list].sort((a, b) => b.title.localeCompare(a.title)); break;
      default: break;
    }
    return list;
  }, [category, sort, priceMax, availability]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">Shop</div>
        <h1 className="font-serif-elegant text-5xl md:text-6xl">All Products</h1>
        <p className="mt-4 text-[#4a4640] max-w-xl mx-auto">Handcrafted herbal &amp; CBD wellness for every ritual of your day.</p>
      </div>

      <div className="flex items-center justify-between border-y border-[#e5decf] py-4 mb-10">
        <button onClick={() => setFiltersOpen(!filtersOpen)} className="flex items-center gap-2 text-sm uppercase tracking-widest">
          <SlidersHorizontal className="w-4 h-4" /> Filter
        </button>
        <div className="text-sm text-[#6f6a5f]">{filtered.length} items</div>
        <div className="flex items-center gap-2">
          <label className="text-sm uppercase tracking-widest hidden sm:inline">Sort</label>
          <div className="relative">
            <select
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="appearance-none bg-transparent border border-[#c9c1ad] pl-3 pr-9 py-2 text-sm focus:outline-none focus:border-[#3a4a3b]"
            >
              <option value="featured">Featured</option>
              <option value="az">Alphabetically, A-Z</option>
              <option value="za">Alphabetically, Z-A</option>
              <option value="price-asc">Price, low to high</option>
              <option value="price-desc">Price, high to low</option>
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        {/* Filters */}
        {filtersOpen && (
          <aside className="space-y-8">
            <div>
              <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-3">Category</div>
              <ul className="space-y-2 text-sm">
                {categories.map(c => (
                  <li key={c}>
                    <button
                      onClick={() => setCategory(c)}
                      className={`link-underline ${category === c ? 'text-[#3a4a3b] font-medium' : 'text-[#4a4640]'}`}
                    >{c}</button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-3">Availability</div>
              <div className="space-y-2 text-sm">
                <label className="flex items-center gap-2">
                  <input type="radio" name="avail" checked={availability === 'all'} onChange={() => setAvailability('all')} /> All
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" name="avail" checked={availability === 'sale'} onChange={() => setAvailability('sale')} /> On Sale
                </label>
              </div>
            </div>
            <div>
              <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-3">Max Price: ${priceMax}</div>
              <input type="range" min="5" max="100" value={priceMax} onChange={e => setPriceMax(Number(e.target.value))} className="w-full accent-[#3a4a3b]" />
              <div className="flex justify-between text-xs text-[#6f6a5f] mt-2"><span>$5</span><span>$100</span></div>
            </div>
          </aside>
        )}

        {/* Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-12">
          {filtered.map(p => <ProductCard key={p.id} product={p} />)}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-24 text-[#6f6a5f]">No products match your filters.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
